import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import TaskEditPanel from "../components/tasks/TaskEditPanel";
import SectionHeader from "../components/ui/SectionHeader";
import { useDeleteTaskMutation, useGetEmployeesQuery, useGetTasksQuery, useUpdateTaskMutation } from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";

const emptyTaskForm = {
  title: "",
  description: "",
  status: "todo",
  priority: "medium",
  assignee_id: "",
  due_date: "",
};

const buildTaskForm = (task) => ({
  title: task?.title || "",
  description: task?.description || "",
  status: task?.status || "todo",
  priority: task?.priority || "medium",
  assignee_id: task?.assignee_id ? String(task.assignee_id) : "",
  due_date: task?.due_date || "",
});

const normalizeTaskPayload = (formValues) => ({
  title: formValues.title.trim(),
  description: formValues.description.trim(),
  status: formValues.status,
  priority: formValues.priority,
  assignee_id: formValues.assignee_id ? Number(formValues.assignee_id) : null,
  due_date: formValues.due_date || null,
});

const EditTaskPage = () => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const { data, isLoading } = useGetTasksQuery();
  const { data: employees } = useGetEmployeesQuery();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const [deleteTask, { isLoading: isDeleting }] = useDeleteTaskMutation();
  const [detailForm, setDetailForm] = useState(emptyTaskForm);
  const [submitError, setSubmitError] = useState("");

  const tasks = useMemo(() => data?.data || [], [data]);
  const selectedTask = useMemo(
    () => tasks.find((task) => Number(task.id) === Number(taskId)) || null,
    [taskId, tasks]
  );

  useEffect(() => {
    setDetailForm(buildTaskForm(selectedTask));
  }, [selectedTask]);

  const handleDetailChange = (field, value) => {
    setDetailForm((current) => ({ ...current, [field]: value }));
  };

  const handleUpdateTask = async (event) => {
    event.preventDefault();

    if (!selectedTask) {
      return;
    }

    const payload = normalizeTaskPayload(detailForm);
    if (!payload.title) {
      setSubmitError("Task title is required.");
      return;
    }

    try {
      await updateTask({ id: selectedTask.id, ...payload }).unwrap();
      setSubmitError("");
      navigate("/tasks");
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to update task."));
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) {
      return;
    }

    try {
      await deleteTask(selectedTask.id).unwrap();
      setSubmitError("");
      navigate("/tasks");
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to delete task."));
    }
  };

  if (isLoading) {
    return <div className="loading-state">Loading task editor...</div>;
  }

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Task Editor"
        title={selectedTask ? `Editing: ${selectedTask.title}` : "Task not found"}
        description="This page opens only after you click a task card from the board."
      />

      {submitError ? <small className="danger-text">{submitError}</small> : null}

      {selectedTask ? (
        <TaskEditPanel
          selectedTask={selectedTask}
          detailForm={detailForm}
          employees={employees}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
          onChange={handleDetailChange}
          onSubmit={handleUpdateTask}
          onDelete={handleDeleteTask}
          onCancel={() => navigate("/tasks")}
        />
      ) : (
        <div className="empty-state">This task could not be found.</div>
      )}
    </div>
  );
};

export default EditTaskPage;
